import moment from 'jalali-moment';
import { useEffect, useState } from 'react';
import endpoints from '../../config/endpoints';
import restApi from '../../services/restApi';
import tools from '../../utils/tools';

const actionTitles = {
  create: 'ایجاد',
  update: 'ویرایش',
  delete: 'حذف',
  bulkPrice: 'تغییر گروهی قیمت',
  bulkPriceRevert: 'بازگشت تغییر گروهی قیمت',
  bulkStatus: 'تغییر گروهی وضعیت',
};

const fieldTitles = {
  price: 'قیمت واحد',
  attributePrice: 'قیمت شاخص',
  count: 'تعداد',
  discountPrice: 'تخفیف',
  discountPercent: 'درصد تخفیف',
  status: 'وضعیت',
  attributeId: 'شاخص',
  variationId: 'تایید مشتری',
  productId: 'محصول',
  title: 'عنوان',
  code: 'کد',
};

const priceFields = ['price', 'discountPrice', 'attributePrice'];

const isPriceField = (field: string) =>
  priceFields.includes((field || '').split('.').pop() || '');

const ActivityLog = ({
  entityType,
  entityId,
  products = [],
  orderProducts = [],
}: {
  entityType: string;
  entityId: any;
  products?: any[];
  orderProducts?: any[];
}) => {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    if (!entityId) return;

    setLoading(true);
    const res = await restApi(endpoints.activityLog).get({ entityType, entityId });
    setLogs(res?.data || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [entityType, entityId]);

  // Resolve the product title for an order-product line.
  const resolveProductTitle = (orderProductId: any, snapshot?: any) => {
    const orderProduct = orderProducts.find((p) => p.id == orderProductId);
    const productId = orderProduct?.productId ?? snapshot?.productId;
    const title =
      orderProduct?.product?.title ||
      products.find((p) => p.id == productId)?.title;
    return title || `محصول #${productId ?? orderProductId}`;
  };

  // Turn a raw change field into a human-readable label.
  const formatField = (field: string) => {
    const match = (field || '').match(/^product\[(\d+)\](?:\.(.+))?$/);
    if (match) {
      const [, orderProductId, sub] = match;
      const title = resolveProductTitle(orderProductId);
      return sub ? `${title} — ${fieldTitles[sub] || sub}` : title;
    }
    return fieldTitles[field] || field;
  };

  const formatValue = (field: string, value: any) => {
    if (value === null || value === undefined) {
      return '—';
    }
    // Whole product added/removed: from/to is a product snapshot object.
    if (typeof value === 'object') {
      const parts: string[] = [];
      if (value.count != null) parts.push(`تعداد: ${value.count}`);
      if (value.price != null) parts.push(`قیمت: ${tools.formatPrice(value.price)}`);
      return parts.length ? parts.join(' | ') : JSON.stringify(value);
    }
    if (typeof value === 'boolean') {
      return value ? 'بله' : 'خیر';
    }
    if (isPriceField(field) && !isNaN(Number(value))) {
      return tools.formatPrice(Number(value));
    }
    return String(value);
  };

  return (
    <div className="infoSection activityLogSection">
      <h1 className="dashBoardTitle">تاریخچه تغییرات</h1>
      {loading ? (
        <p className="sideBarTitle">در حال بارگذاری...</p>
      ) : logs.length === 0 ? (
        <p className="sideBarTitle">تغییری ثبت نشده است</p>
      ) : (
        <div className="activityLogList">
          {logs.map((log) => (
            <div className="activityLogItem" key={log.id}>
              <div className="activityLogHeader">
                <span className="activityLogAction">
                  {actionTitles[log.action] || log.action}
                </span>
                <span className="activityLogAdmin">
                  {log.admin?.name || 'کاربر حذف شده'}
                </span>
                <span className="activityLogDate">
                  {moment(log.createdAt).format('jYYYY/jMM/jDD HH:mm')}
                </span>
              </div>
              {log.description && (
                <p className="activityLogDescription">{log.description}</p>
              )}
              {Array.isArray(log.changes) && log.changes.length > 0 && (
                <table className="activityLogTable">
                  <thead>
                    <tr>
                      <th>فیلد</th>
                      <th>از</th>
                      <th>به</th>
                    </tr>
                  </thead>
                  <tbody>
                    {log.changes.map((change, index) => (
                      <tr key={index}>
                        <td>{formatField(change.field)}</td>
                        <td>{formatValue(change.field, change.from)}</td>
                        <td>{formatValue(change.field, change.to)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityLog;
